import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { CheckCircleFilledIcon } from '#/shared/ui/icons'
import { Skeleton } from '#/shared/ui/skeleton'

import { usePaymentDetail } from '../lib/use-payment-detail'
import type {
  PaymentDetailRowView,
  PaymentDetailView,
  PaymentProofFile,
  PaymentTransactionView,
} from '../lib/use-payment-detail'
import { useSendPaymentReceipt } from '../lib/use-send-payment-receipt'

const DETAIL_PREFIX = 'orderDetails.comment.payment.detail'

function isHttpUrl(value: string) {
  return /^https?:\/\/\S+$/i.test(value.trim())
}

interface PaymentRecordedDetailProps {
  paymentId: string
  enabled: boolean
}

function Hero({ view }: { view: PaymentDetailView }) {
  return (
    <div className="flex w-full flex-col items-center gap-3">
      <span
        aria-hidden
        className="flex size-16 shrink-0 items-center justify-center rounded-full bg-paper-signed-bg"
      >
        <CheckCircleFilledIcon className="size-10" />
      </span>
      <span className="text-xl font-medium text-gray-900">
        {view.amount} {view.description}
      </span>
      <span className="text-xs text-gray-500">{view.meta}</span>
    </div>
  )
}

function KVCard({ rows }: { rows: ReadonlyArray<PaymentDetailRowView> }) {
  return (
    <div className="flex w-full flex-col rounded-md bg-gray-50 px-3.5">
      {rows.map((row, index) => (
        <Fragment key={row.label}>
          {index > 0 && <div className="h-px w-full shrink-0 bg-gray-200" />}
          <div className="flex items-baseline justify-between gap-3 py-2 text-xs">
            <span className="text-gray-500">{row.label}</span>
            {isHttpUrl(row.value) ? (
              <a
                href={row.value}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  'text-right break-all text-primary hover:underline focus-visible:outline-none focus-visible:focus-ring',
                  row.strong && 'font-medium',
                )}
              >
                {row.value}
              </a>
            ) : (
              <span
                className={cn(
                  'text-right break-all text-gray-900',
                  row.strong && 'font-medium',
                )}
              >
                {row.value}
              </span>
            )}
          </div>
        </Fragment>
      ))}
    </div>
  )
}

function ProofCard({ file }: { file: PaymentProofFile }) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full items-center gap-3 rounded-md bg-gray-50 px-3 py-2.5">
      {file.isImage ? (
        <img
          src={file.url}
          alt={file.name}
          className="size-10 shrink-0 rounded object-cover"
        />
      ) : (
        <span
          aria-hidden
          className="flex size-10 shrink-0 items-center justify-center rounded bg-paper-avatar text-2xs font-bold tracking-wide text-gray-500"
        >
          {file.ext}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-gray-900">
          {file.name}
        </span>
        <span className="text-xs text-paper-text-faint">{file.meta}</span>
      </div>
      <a
        href={file.url}
        target="_blank"
        rel="noreferrer"
        className="shrink-0 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:focus-ring"
      >
        {t(`${DETAIL_PREFIX}.proofView`)}
      </a>
    </div>
  )
}

function TransactionBlock({
  tx,
  receiptEmail,
  onSendReceipt,
  isSending,
}: {
  tx: PaymentTransactionView
  receiptEmail: string | undefined
  onSendReceipt: (transactionId: string, email: string | undefined) => void
  isSending: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full flex-col gap-2.5">
      <div className="flex w-full flex-col gap-1.25">
        <span className="text-2xs text-gray-500">{tx.title}</span>
        <KVCard rows={tx.rows} />
      </div>
      {tx.canSendReceipt && (
        <button
          type="button"
          disabled={isSending}
          onClick={() => onSendReceipt(tx.id, receiptEmail)}
          className="self-start text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:focus-ring disabled:opacity-60"
        >
          {receiptEmail
            ? t(`${DETAIL_PREFIX}.sendReceiptTo`, { email: receiptEmail })
            : t(`${DETAIL_PREFIX}.sendReceipt`)}
        </button>
      )}
      {tx.files.length > 0 && (
        <div className="flex w-full flex-col gap-1.25">
          <span className="text-2xs text-gray-500">
            {t(`${DETAIL_PREFIX}.proof`)}
          </span>
          {tx.files.map((file) => (
            <ProofCard key={file.id} file={file} />
          ))}
        </div>
      )}
    </div>
  )
}

function StateMessage({ message }: { message: string }) {
  return (
    <div className="flex min-h-32 flex-1 items-center justify-center px-5">
      <span className="text-sm text-gray-500">{message}</span>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pt-4 pb-4">
      <div className="flex w-full flex-col items-center gap-3 py-2">
        <Skeleton className="size-16 rounded-full" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-56" />
      </div>
      <Skeleton className="h-48 w-full rounded-md" />
    </div>
  )
}

export function PaymentRecordedDetail({
  paymentId,
  enabled,
}: PaymentRecordedDetailProps) {
  const { t } = useTranslation()
  const { view, isLoading, isError } = usePaymentDetail(paymentId, enabled)
  const { send, isSending } = useSendPaymentReceipt()

  if (isLoading) return <DetailSkeleton />
  if (isError || !view) {
    return <StateMessage message={t(`${DETAIL_PREFIX}.loadError`)} />
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pt-5 pb-5">
      <Hero view={view} />
      {view.transactions.map((tx, index) => (
        <Fragment key={tx.id}>
          {index > 0 && <div className="h-px w-full shrink-0 bg-gray-200" />}
          <TransactionBlock
            tx={tx}
            receiptEmail={view.receiptEmail}
            onSendReceipt={(id, email) => void send(id, email)}
            isSending={isSending}
          />
        </Fragment>
      ))}
    </div>
  )
}
